import React, { useEffect, useRef, useState } from "react";
import { pipe } from "fp-ts/lib/function";
import { array } from "fp-ts";
import { TCaseTimelineRoadblockOption } from "../../../../domain/codecs/form/CaseTimelineForm";
import { RoadblockStatus_displayString } from "../../../../domain/codecs/form/RoadblockForm";
import { Relative } from "../BuildingBlocks/Relative";
import { Absolute } from "../BuildingBlocks/Absolute";
import { Background } from "../BuildingBlocks/Background";
import { Padding } from "../BuildingBlocks/Padding";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { FontQuicksand } from "../BuildingBlocks/FontQuicksand";
import { Text } from "../BuildingBlocks/Text";
import { Shape } from "../BuildingBlocks/Shape";
import { CRMIcon } from "../CRMIcon/CRMIcon";
import { CRMColors } from "../../models/CRMColors";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { capitaliseFirst, underscoreCaseToNormalCase } from "../../../../shared/src/utilsByDomain/string";

type TCaseTimelineRoadblockPickerProps = {
    options: Array<TCaseTimelineRoadblockOption>;
    selectedRoadblockId: string;
    onSelect: (target: { roadblock_id: string, chats_id: string }) => void;
};

const isLive = (option: TCaseTimelineRoadblockOption): boolean =>
    option.status === "open" || option.status === "awaiting_signoff" || option.status === "blocked";

const optionName = (option: TCaseTimelineRoadblockOption): string =>
    pipe(underscoreCaseToNormalCase(option.type), capitaliseFirst);

export const CRMCaseTimelineRoadblockPicker = (props: React.PropsWithChildren<TCaseTimelineRoadblockPickerProps>): JSX.Element => {

    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current !== null && e.target instanceof Node && !ref.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const selected = props.options.find((option) => option.id === props.selectedRoadblockId);

    const matches = props.options.filter((option) =>
        optionName(option).toLowerCase().includes(query.toLowerCase())
    );
    const liveMatches = matches.filter(isLive);
    const otherMatches = matches.filter((option) => !isLive(option));

    const onPick = (option: TCaseTimelineRoadblockOption) => {
        props.onSelect({ roadblock_id: option.id, chats_id: option.chats_id });
        setIsOpen(false);
        setQuery("");
    };

    const renderItem = (option: TCaseTimelineRoadblockOption) => (
        <Background
            key={option.id}
            borderRadius="8px"
            padding={`${CRMSpacing.TINY} ${CRMSpacing.SMALL}`}
            cursor="pointer"
            onClick={() => onPick(option)}
        >
            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center" childSize="1fr auto">
                <FontQuicksand>
                    <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_INK} weight={600}>
                        {optionName(option)}
                    </Text>
                </FontQuicksand>
                <FontQuicksand>
                    <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4} weight={600}>
                        {RoadblockStatus_displayString(option.status)}
                    </Text>
                </FontQuicksand>
            </SpacingRow>
        </Background>
    );

    return (
        <div ref={ref}>
            <Relative>
                {/* PILL */}
                <Background
                    color={CRMColors.NEUTRAL_PAPER}
                    borderRadius="9px"
                    borderDefinition={`solid 1px ${CRMColors.NEUTRAL_8}`}
                    padding={`${CRMSpacing.TINY} ${CRMSpacing.SMALL}`}
                    cursor="pointer"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                        <Shape
                            width="8px"
                            height="8px"
                            borderRadius="50%"
                            color={selected !== undefined ? CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2 : CRMColors.NEUTRAL_6}
                        />
                        <FontQuicksand>
                            <Text
                                size={CRMFontSizes.SMALL}
                                color={selected !== undefined ? CRMColors.NEUTRAL_INK : CRMColors.NEUTRAL_4}
                                weight={selected !== undefined ? 600 : 500}
                            >
                                {selected !== undefined ? optionName(selected) : "Select a roadblock"}
                            </Text>
                        </FontQuicksand>
                        <CRMIcon iconName="down-arrow" size="14px" colour="neutral-4" />
                    </SpacingRow>
                </Background>

                {/* POPOVER */}
                {isOpen &&
                    <Absolute top="42px" left="0px" zIndex={30}>
                        <Background
                            color={CRMColors.NEUTRAL_PAPER}
                            borderRadius="12px"
                            borderDefinition={`solid 1px ${CRMColors.NEUTRAL_10}`}
                            width="320px"
                        >
                            <SpacingColumn>
                                {/* SEARCH */}
                                <Padding type="custom" spacing={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}>
                                    <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                        <CRMIcon iconName="search-list" size="15px" colour="neutral-6" />
                                        <input
                                            className="crm-case-timeline-picker__search"
                                            autoFocus
                                            placeholder="Search roadblocks…"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                        />
                                    </SpacingRow>
                                </Padding>

                                <Shape height="1px" width="100%" color={CRMColors.NEUTRAL_10} />

                                {/* LIST */}
                                <div className="crm-case-timeline-picker__list">
                                    <Padding type="custom" spacing={CRMSpacing.TINY}>
                                        <SpacingColumn spacing="1px">
                                            {liveMatches.length > 0 &&
                                                <Padding type="custom" spacing={`${CRMSpacing.TINY} ${CRMSpacing.SMALL} 2px`}>
                                                    <FontQuicksand><Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4} weight={700}>OPEN</Text></FontQuicksand>
                                                </Padding>
                                            }
                                            {liveMatches.map(renderItem)}

                                            {otherMatches.length > 0 &&
                                                <Padding type="custom" spacing={`${CRMSpacing.SMALL} ${CRMSpacing.SMALL} 2px`}>
                                                    <FontQuicksand><Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4} weight={700}>CLOSED</Text></FontQuicksand>
                                                </Padding>
                                            }
                                            {otherMatches.map(renderItem)}

                                            {matches.length === 0 &&
                                                <Padding type="custom" spacing={CRMSpacing.SMALL}>
                                                    <FontQuicksand><Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4} weight={500}>No roadblocks match.</Text></FontQuicksand>
                                                </Padding>
                                            }
                                        </SpacingColumn>
                                    </Padding>
                                </div>
                            </SpacingColumn>
                        </Background>
                    </Absolute>
                }
            </Relative>
        </div>
    );
};
